/**
 * A tiny arithmetic evaluator for the "2 + 2 =" behaviour.
 *
 * Hand-written rather than eval() or Function(): note bodies are user content
 * that also syncs from other devices, and handing that to a JS evaluator is a
 * code-execution hole. This only ever produces a number.
 *
 * Grammar:
 *   expr   := term (('+' | '-') term)*
 *   term   := factor (('*' | '×' | '/' | '÷') factor)*
 *   factor := unary ('^' factor)?          -- right associative
 *   unary  := ('+' | '-')* postfix
 *   postfix:= primary '%'*                 -- percent is /100
 *   primary:= number | '(' expr ')'
 */

type Tok = { kind: 'num'; value: number } | { kind: 'op'; value: string }

const OPS = new Set(['+', '-', '*', '/', '^', '(', ')', '%', '×', '÷'])

function tokenize(input: string): Tok[] | null {
  const toks: Tok[] = []
  let i = 0
  while (i < input.length) {
    const c = input[i]
    if (c === ' ' || c === '\t') { i++; continue }
    if (c >= '0' && c <= '9') {
      let j = i
      while (j < input.length && /[0-9]/.test(input[j])) j++
      if (input[j] === '.') { j++; while (j < input.length && /[0-9]/.test(input[j])) j++ }
      toks.push({ kind: 'num', value: Number(input.slice(i, j)) })
      i = j
      continue
    }
    if (c === '.') {
      let j = i + 1
      while (j < input.length && /[0-9]/.test(input[j])) j++
      if (j === i + 1) return null
      toks.push({ kind: 'num', value: Number(input.slice(i, j)) })
      i = j
      continue
    }
    if (OPS.has(c)) { toks.push({ kind: 'op', value: c }); i++; continue }
    return null // anything else means this is prose, not a sum
  }
  return toks
}

function parse(toks: Tok[]): number | null {
  let pos = 0
  const peek = () => toks[pos]
  const eatOp = (...vals: string[]) => {
    const t = peek()
    if (t && t.kind === 'op' && vals.includes(t.value)) { pos++; return t.value }
    return null
  }

  const primary = (): number | null => {
    const t = peek()
    if (!t) return null
    if (t.kind === 'num') { pos++; return t.value }
    if (t.value === '(') {
      pos++
      const v = expr()
      if (v === null || !eatOp(')')) return null
      return v
    }
    return null
  }

  const postfix = (): number | null => {
    let v = primary()
    if (v === null) return null
    while (eatOp('%')) v = v / 100
    return v
  }

  const unary = (): number | null => {
    const sign = eatOp('+', '-')
    if (sign === null) return postfix()
    const v = unary()
    return v === null ? null : (sign === '-' ? -v : v)
  }

  const factor = (): number | null => {
    const base = unary()
    if (base === null) return null
    if (eatOp('^')) {
      const exp = factor()
      return exp === null ? null : base ** exp
    }
    return base
  }

  const term = (): number | null => {
    let v = factor()
    if (v === null) return null
    for (;;) {
      const op = eatOp('*', '/', '×', '÷')
      if (!op) return v
      const rhs = factor()
      if (rhs === null) return null
      if (op === '/' || op === '÷') {
        if (rhs === 0) return null // leave "5/0=" alone rather than printing Infinity
        v /= rhs
      } else v *= rhs
    }
  }

  const expr = (): number | null => {
    let v = term()
    if (v === null) return null
    for (;;) {
      const op = eatOp('+', '-')
      if (!op) return v
      const rhs = term()
      if (rhs === null) return null
      v = op === '+' ? v + rhs : v - rhs
    }
  }

  const value = expr()
  return pos === toks.length ? value : null
}

/** Trims float noise: 0.1 + 0.2 should read 0.3, not 0.30000000000000004. */
export function formatResult(n: number): string {
  if (!Number.isFinite(n)) return ''
  const rounded = Number(n.toPrecision(12))
  return String(Number(rounded.toFixed(10)))
}

/**
 * Evaluates an arithmetic expression, or returns null if it is not one.
 * Requires at least one operator so a bare "42 =" is left alone.
 */
export function evaluate(input: string): string | null {
  const trimmed = input.trim()
  if (!trimmed) return null
  if (!/[+\-*/^×÷]/.test(trimmed)) return null
  if (!/\d/.test(trimmed)) return null
  // Strip thousands separators (1,200 -> 1200) before tokenizing; repeat so
  // 1,234,567 collapses fully.
  let cleaned = trimmed
  for (;;) {
    const next = cleaned.replace(/(\d),(\d)/g, '$1$2')
    if (next === cleaned) break
    cleaned = next
  }
  const toks = tokenize(cleaned)
  if (!toks || !toks.length) return null
  const value = parse(toks)
  if (value === null || !Number.isFinite(value)) return null
  return formatResult(value)
}
